import React, { useMemo } from 'react'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { getDurationPackageOptions } from 'selectors/offerPage/offerPageSelectors'
import useSuggestedPackageOption from 'hooks/OfferPage/useSuggestedPackageOption'
import { buildRoomPackageOptionsViewOrder } from 'lib/offer/offerPageUtil'
import { EmptyArray, groupBy, min, sortByAll, sum, max } from 'lib/array/arrayUtils'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FlashHotelMobileSameRoomOption from './FlashHotelMobileSameRoomOption'

function sortByAvailability(options: Array<App.OfferPackageOptionView>, roomsLength: number) {
  return options[0].availableRooms ?? 0 - roomsLength
}

function sortByPrice(options: Array<App.OfferPackageOptionView>) {
  return min(options, option => option.hotelTotal)
}

function sortBySelectedAvailability(
  options: Array<App.OfferPackageOptionView>,
  showSelection: boolean,
  rooms: Array<OfferPageRoom>,
) {
  if (!showSelection) return 0
  const availableRoomsTotal = sum(options, option => option.availableRooms ?? 0)
  if (availableRoomsTotal < rooms.length) return 0
  // Same for both rooms
  const optionRoomType = options[0].package.roomType?.id
  const isSelected = rooms.some(selectedRoom => selectedRoom.roomId === optionRoomType)
  return isSelected ? 1 : 0
}

interface Props {
  offer: App.HotelOffer
  rooms: Array<OfferPageRoom>
  packageOptions: Array<App.OfferPackageOptionView>
  showSelection?: boolean
  headerRef?: React.RefObject<HTMLDivElement | null>
}

function FlashHotelMobileSameRoomOptions(props: Props) {
  const {
    offer,
    rooms,
    packageOptions,
    showSelection = false,
    headerRef,
  } = props

  const suggestedPackageOption = useSuggestedPackageOption()

  const isMultiRoom = rooms.length > 1

  // Compute available room count for urgency display
  const availableRoomCountByRoomId = useMemo(() => {
    const availablePackageOptions = packageOptions.filter(packageOption => (packageOption.availableRooms || 0) > 0)
    const packageView = max(availablePackageOptions, packageView => packageView.package.roomType?.stats.booked || 0)
    if (packageView?.availableRooms && packageView.package.roomType?.id && packageView.availableRooms > 0 && packageView.availableRooms <= 10) {
      return new Map([[packageView.package.roomType?.id, packageView?.availableRooms]])
    }
    return new Map()
  }, [packageOptions])

  // Group and sort packages by room type
  const packageOptionsByRoom = useMemo(() => {
    if (isMultiRoom) {
      // Multi-room sorting: prioritize availability for all rooms, then price, then selection
      const rawOptions = Array.from(groupBy(packageOptions, (pkg) => pkg?.package.roomType?.id).values())
      const sorted = sortByAll(rawOptions, [
        { selector: pkg => sortByAvailability(pkg, rooms.length), direction: 'desc' },
        { selector: sortByPrice, direction: 'asc' },
        { selector: pkg => sortBySelectedAvailability(pkg, showSelection, rooms), direction: 'desc' },
      ])
      return sorted.map(packageViews => ({
        roomId: packageViews[0].package.roomType?.id ?? '',
        packageViews,
      }))
    } else {
      // Single-room: use existing ordering logic
      return buildRoomPackageOptionsViewOrder(packageOptions)
    }
  }, [packageOptions, rooms, showSelection, isMultiRoom])

  return (
    <VerticalSpacer gap={8}>
      {packageOptionsByRoom.map((item) => (
        <FlashHotelMobileSameRoomOption
          key={item.roomId}
          offer={offer}
          packageViews={item.packageViews}
          availableRoomCount={availableRoomCountByRoomId.get(item.roomId)}
          showSelection={showSelection}
          headerRef={headerRef}
          suggestedPackageOption={suggestedPackageOption}
        />
      ))}
    </VerticalSpacer>
  )
}

type MappedProps = Pick<Props,
  | 'offer'
  | 'rooms'
  | 'packageOptions'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  offer: state.offer as App.HotelOffer,
  rooms: state.rooms ?? EmptyArray,
  packageOptions: getDurationPackageOptions(state, appState),
}), FlashHotelMobileSameRoomOptions)
